import { Heading, SubHeading, Title } from "../components/Text";

import "./Experience.css";

export const experience = [
  {
    company: "OWL AI, Boulder, CO",
    startDate: "Apr 2026",
    endDate: "Present",
    description:
      "Lead engineering for OWL, a real-time computer vision and multimodal AI platform that turns live video into officiating decisions, broadcast storytelling, and fan engagement insights. Own architecture, delivery, and organizational design across the full product surface. Partner directly with the CEO and CTO on roadmap, technical strategy, and enterprise readiness for league, federation, and broadcast customers. Tech Stack: React/TypeScript, Python, Go, GCP (GKE, Cloud Run, Cloud Spanner, Gemini), NVIDIA CUDA.",
    roles: [
      {
        title: "VP, Engineering",
        highlights: [
          "Shipped autonomous line-calling for professional pickleball, resolving in/out decisions at broadcast frame rates with 99.9% agreement against human-referee ground truth. The system is now the trusted source for contested challenges.",
          "Delivered the X Games partnership across three summer events (Sacramento, Chiba, New Orleans). Architected an iPhone-based capture pipeline, judge-facing Session Assist on iPad, and a Cloud Run/GKE and Cloud Spanner metrics backend that return run review and metrics to judges within 5-6 seconds.",
          "Established the company's first production readiness program, maintaining quality across six axes: observability, reliability, security, operations, data, and scalability.",
          "Set the engineering roadmap and technical thesis with the CEO. Anchored Owl's defensibility in officiating-grade ground-truth data, real-time processing at broadcast frame rates, and deterministic outcomes for subjective calls, and directed investment in edge computing and world models/video transformers.",
        ],
      },
    ],
  },
  {
    company: "Meta, Burlingame, CA (Hybrid)",
    startDate: "Oct 2024",
    endDate: "Apr 2026",
    description:
      "Architected and maintained video playback infrastructure powering VR media for over 8.5 million MAU across the Quest headset ecosystem. Contributed across frontend (React Native), SDK (Java/Kotlin), and performance-critical playback (C++). Ensured video reliability, performance, and consistency across diverse application layers.",
    roles: [
      {
        title: "Senior Software Engineer",
        highlights: [
          "Engineered a highly-performant, client-side ML conversion engine to offload 2D-to-3D video processing from the server, maintaining high-fidelity playback and an 8% increase in user engagement.",
          "Delivered media extensions via Maven Central that enable rapid integration of immersive video for internal and third-party partners (incl. Disney+), reducing integration time from months to days.",
          "Developed and deployed internal AI-driven codemod tools that resolved 5,200 code quality issues, saving approximately 1,780 engineering hours in manual refactoring and cleanup.",
          "Spearheaded a testing initiative utilizing an AI-driven codemod; provided foundational test templates and prompt engineering to autonomously generate production-ready tests, increasing test coverage from 4% to 50% in six months.",
          "Optimized core C++/Java media runtime performance to sustain a playback failure rate of below 1% across all production applications, ensuring strict adherence to critical SLAs.",
          "Enabled market-leading immersive experiences by bridging frontend and media-runtime layers for features like 360-degree video, 3D playback (utilizing Dolby Vision/MV-HEVC), and dynamic-depth subtitle rendering.",
        ],
      },
    ],
  },
  {
    company: "Percipient.ai, Santa Clara, CA (Remote)",
    startDate: "May 2018",
    endDate: "Sep 2024",
    description: (
      <span>
        <span>
          Led architectural design and implementation of the&nbsp;
          <a
            href="https://percipient.ai/technology/"
            target="_blank"
            rel="noreferrer"
          >
            Mirage Intelligence Analysis Platform
          </a>
          , an AI-enabled computer vision system that processes both live and
          historical visual/multi-INT data for analysts. Built both frontend UI
          and backend APIs to support embeddings, search, detection, and
          correlation workflows. Collaborated closely with the Chief Product
          Officer and lead Project Manager to shape and contribute to the
          product roadmap. Promoted twice&mdash;from Senior Engineer to Staff
          Engineer and then to Principal Engineer&mdash;while concurrently
          managing the frontend engineering team. Consistently recognized for
          outstanding performance and adaptability, demonstrating a commitment
          to excellence in both technical and managerial capacities. Tech Stack:
          React/TypeScript, Django, Postgres + Redis.
        </span>
      </span>
    ),
    roles: [
      {
        startDate: "Nov 2022",
        endDate: "Sep 2024",
        title: "Principal Engineer/Engineering Manager",
        highlights: [
          "Developed intuitive, interactive UI visualizations including geospatial mapping, real-time/full-motion video feeds, and detection overlays to enable analysts to confirm/reject/correct detections, thereby driving model retraining and performance improvements over time.",
          "Maintained strong developer velocity as an EM by reviewing a large share of PRs, contributing core functionality, and driving process improvements across the frontend and backend.",
          "Collaborated with product & operations to ensure Mirage's AI modules delivered in high-security and integration-ready form; guaranteed data validity, model accuracy, and scalable ingestion of large visual datasets.",
          "Led the UI team through a startup's high-growth journey, initiating process changes for scalability and implementing a full testing strategy and TypeScript for elevated code quality.",
          "Contributed to the development and successful implementation of a comprehensive annual performance review process, enhancing organizational efficiency and fostering a culture of excellence.",
        ],
      },
    ],
  },
  {
    company: "Impekable, San Jose, CA (Remote)",
    startDate: "May 2017",
    endDate: "May 2018",
    description:
      "Acted as an outsourced Software Engineer at Impekable, a dynamic digital agency, specializing in custom web and native applications for clients including Google, Accenture, and Percipient.ai, ultimately leading to a full-time role with Percipient.ai. Primary responsibilities included building tailored solutions from customer-defined specifications. Tech Stack: JavaScript, React, Angular, React Native, and Node.js.",
    roles: [
      {
        title: "Software Engineer",
        highlights: [
          "Engineered RESTful APIs to ensure consistent and intuitive customer experiences, enhancing the functionality of web and native applications.",
          "Implemented robust data security measures, incorporating JSON web-token verification and advanced password hashing/salting techniques.",
          "Managed internal project with Google, leading the development of an internal tool for UI/UX auditing, ensuring adherence to Google design standards.",
        ],
      },
    ],
  },
  {
    company: "Appirio, San Francisco, CA (Remote)",
    startDate: "Sep 2014",
    endDate: "Dec 2016",
    description:
      "Led digital transformation initiatives for  Appirio, a global leader in technology consulting and cloud-based solutions, specializing in design, construction, and implementation of innovative cloud solutions.",
    roles: [
      {
        title: "Software Integration Developer",
        highlights: [
          "Developed pivotal inbound and outbound interfaces to Workday in Java, ensuring smooth transitions for clients like Bridgewater Financial Services and CNS Wholesale Grocers.",
          "Proficient in designing, building, and deploying custom Workday reports, leveraging JavaScript, CSS, and HTML to enhance reporting capabilities.",
          "Demonstrated effective leadership by guiding a small team through early-stage project planning, ensuring successful project execution.",
          "Spearheaded the development of third-party integrations, streamlining data transfer from Workday into payroll systems for operational efficiency.",
        ],
      },
    ],
  },
];

function Highlights({ highlights }) {
  if (!highlights) return null;

  return (
    <ul className="Experience-highlight-list">
      {highlights.map((h, i) => (
        <li key={i} className="Experience-highlight-list-item">
          {h}
        </li>
      ))}
    </ul>
  );
}

function HeadingContainer({ children }) {
  return <div className="Experience-heading-container">{children}</div>;
}

function Experience({ company, description, roles, startDate, endDate }) {
  const { highlights, title } = roles[0];

  return (
    <div>
      <HeadingContainer>
        <Heading>{company}</Heading>
        <SubHeading>
          {startDate} - {endDate}
        </SubHeading>
      </HeadingContainer>
      <Title style={{ marginBottom: 12 }}>{title}</Title>
      <p>{description}</p>
      <Highlights highlights={highlights} />
    </div>
  );
}

export default Experience;
